/**
 * Administration's database-backed Users directory. Groups live in `groups-panel.ts`.
 *
 * A person has one directly assigned Explorer role (Viewer or Member), any
 * optional module roles, and whatever their groups give them. Inherited roles
 * are shown as chips that link to their group - never as a switch that could
 * not revoke them. The effective-access table comes from the server's access
 * evaluator (`GET /api/admin/access/users/{id}`), the same one its API guards
 * use, so "allowed" here means allowed there. See `docs/plans/access-management.md`.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo, catalogAvailable, catalogAvailabilityKnown, isRootUser, myPermissions, myUserId } from '../../state/store.ts';
import {
  getAccessExplanation,
  getAdminUser,
  listAdminUsers,
  setUserActive,
  setUserBaseRole,
  setUserRole,
  setUserRoot,
} from '../../api/endpoints.ts';
import type { AccessExplanation, AdminUser, UserDirectory, UserRoleBinding } from '../../api/types.ts';
import { loadMe } from '../../state/me.ts';
import { confirmAction, confirmWithText } from '../../ui/confirm-dialog.ts';
import { showToast } from '../../ui/toast.ts';
import { timeAgo } from '../../utils/dates.ts';
import { userMessage } from '../../api/errors.ts';
import {
  OPEN_USER_EVENT,
  loadRoleCatalog,
  openAdminGroup,
  roleCatalog,
  roleLabel,
  rolesOfKind,
  takePendingUser,
} from './role-catalog.ts';

const PAGE_SIZE = 50;

function when(value: string | null, relative = false): TemplateResult {
  if (!value) return html`<span class="people-muted">Not recorded</span>`;
  return html`<time datetime=${value} title=${new Date(value).toISOString()}>
    ${relative ? timeAgo(value, 'long') : new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
  </time>`;
}

const APP_KINDS = new Set(['base', 'module', 'admin']);

/** Whole-app role bindings - not object sharing. */
function appBindings(user: AdminUser): UserRoleBinding[] {
  return user.bindings.filter((b) => b.scope === 'global' && APP_KINDS.has(b.role_kind));
}

export function directRole(user: AdminUser, role: string): boolean {
  return appBindings(user).some((b) => b.role === role && b.source === 'user');
}

/** The one directly assigned Explorer role, or null. */
export function baseRole(user: AdminUser): string | null {
  return appBindings(user).find((b) => b.source === 'user' && b.role_kind === 'base')?.role ?? null;
}

/** Roles that come through a group, with the group - for chips, not switches. */
export function inheritedRoles(user: AdminUser): UserRoleBinding[] {
  return appBindings(user).filter((b) => b.source === 'group');
}

export function standing(user: AdminUser): string {
  if (!user.is_active) return 'Inactive';
  if (user.is_root) return 'Root';
  const apps = appBindings(user);
  if (apps.some((b) => b.role_kind === 'admin')) return 'Administrator';
  const direct = baseRole(user);
  if (direct) return roleLabel(direct);
  const inherited = apps.find((b) => b.role_kind === 'base');
  return inherited ? `${roleLabel(inherited.role)} (group)` : 'No Explorer role';
}

/** Optional roles, direct and inherited, by display name. */
function optionalRoles(user: AdminUser): string[] {
  const names = appBindings(user).filter((b) => b.role_kind === 'module').map((b) => b.role_name);
  return [...new Set(names)];
}

@customElement('people-panel')
export class PeoplePanel extends LightElement {
  private store = new StoreController(this, [myPermissions, isRootUser, myUserId, catalogAvailable, catalogAvailabilityKnown, roleCatalog]);
  @state() private data: UserDirectory = { users: [], total: 0, roots: [] };
  @state() private loading = false;
  @state() private error = '';
  @state() private search = '';
  @state() private status = '';
  @state() private page = 0;
  @state() private openId: string | null = null;
  @state() private pending: string | null = null;
  @state() private transferTo = '';
  /** The open person's effective access, from the server's evaluator. */
  @state() private access: AccessExplanation | null = null;
  @state() private accessError = '';
  private requested = false;
  private revision = 0;
  private searchTimer: number | undefined;

  override willUpdate(): void {
    if (!this.requested && canDo('users:read') && catalogAvailabilityKnown.value && catalogAvailable.value) {
      this.requested = true;
      void loadRoleCatalog();
      void this.load().then(() => {
        const asked = takePendingUser();
        if (asked) void this.openById(asked);
      });
    }
  }

  private onOpenUser = (e: Event): void => {
    const id = (e as CustomEvent<{ id: string }>).detail?.id;
    if (id && this.requested) {
      takePendingUser();
      void this.openById(id);
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener(OPEN_USER_EVENT, this.onOpenUser);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener(OPEN_USER_EVENT, this.onOpenUser);
    window.clearTimeout(this.searchTimer);
    this.revision++;
  }

  /** Open one person, fetching them when they are not on this page of the list. */
  private async openById(id: string): Promise<void> {
    let user = this.data.users.find((u) => u.id === id);
    if (!user) {
      try {
        user = await getAdminUser(id);
        this.data = { ...this.data, users: [user, ...this.data.users] };
      } catch (e) {
        showToast('error', userMessage(e));
        return;
      }
    }
    if (this.openId !== id) this.toggleDetails(user);
  }

  private async loadAccess(userId: string): Promise<void> {
    this.accessError = '';
    try {
      const access = await getAccessExplanation(userId);
      if (this.openId === userId) this.access = access;
    } catch (e) {
      if (this.openId === userId) this.accessError = userMessage(e);
    }
  }

  private async load(): Promise<void> {
    const revision = ++this.revision;
    this.loading = true;
    this.error = '';
    try {
      const data = await listAdminUsers({ q: this.search, limit: PAGE_SIZE, offset: this.page * PAGE_SIZE,
        ...(this.status ? { active: this.status === 'active' } : {}) });
      if (revision === this.revision) {
        if (this.page > 0 && this.page * PAGE_SIZE >= data.total) {
          this.page = Math.max(0, Math.ceil(data.total / PAGE_SIZE) - 1);
          await this.load();
        } else this.data = data;
      }
    } catch (e) {
      if (revision === this.revision) this.error = userMessage(e);
    } finally {
      if (revision === this.revision) this.loading = false;
    }
  }

  private searchChanged(value: string): void {
    this.search = value;
    // Invalidate an earlier filter request before the debounce fires.
    this.revision++;
    window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => { this.page = 0; void this.load(); }, 300);
  }

  private toggleDetails(user: AdminUser): void {
    this.openId = this.openId === user.id ? null : user.id;
    this.access = null;
    if (this.openId) void this.loadAccess(user.id);
    const roots = this.data.roots.filter((r) => r.id !== user.id);
    this.transferTo = roots.find((r) => r.id === myUserId.value)?.id ?? roots[0]?.id ?? '';
  }

  /** Take an updated person from the server, and follow through on what it touches. */
  private async absorbUser(updated: AdminUser): Promise<void> {
    this.data = { ...this.data, users: this.data.users.map((u) => u.id === updated.id ? updated : u) };
    // Your own change reaches your own navigation now, not at the next minute's
    // refresh: `/api/me` carries roles, flags and the access decisions together.
    if (updated.id === myUserId.value) await loadMe().catch(() => {});
    if (this.openId === updated.id) void this.loadAccess(updated.id);
    void loadRoleCatalog(true);
  }

  /** What is left after a direct role goes: the groups that still give it. */
  private stillInherited(user: AdminUser, kind: 'base' | string): string[] {
    return inheritedRoles(user)
      .filter((b) => (kind === 'base' ? b.role_kind === 'base' : b.role === kind))
      .map((b) => `${b.role_name} from ${b.group_name ?? 'a group'}`);
  }

  private async changeBase(user: AdminUser, role: string | null): Promise<void> {
    const previous = baseRole(user);
    if (this.pending || role === previous) return;
    const rank = (r: string | null): number => (r === 'explorer_member' ? 2 : r === 'explorer_viewer' ? 1 : 0);
    if (rank(role) < rank(previous)) {
      const kept = this.stillInherited(user, 'base');
      const ok = await confirmAction({
        title: `Change ${user.display_name} to ${role ? roleLabel(role) : 'no Explorer role'}?`,
        message: kept.length
          ? `Their direct ${roleLabel(previous)} goes, but they still inherit ${kept.join(', ')} - so what they can do does not shrink until they leave that group.`
          : role
            ? `They lose what ${roleLabel(previous)} adds: creating, editing, deleting and sharing their own charts, folders and dashboards. Existing work stays theirs.`
            : 'They keep only what Features gives everyone: no saved content, no sharing.',
        confirmLabel: 'Change role',
        destructive: !kept.length,
      });
      if (!ok) return;
    }
    this.pending = user.id;
    try {
      await this.absorbUser(await setUserBaseRole(user.id, role));
      showToast('success', `${user.display_name} is now ${role ? roleLabel(role) : 'without an Explorer role'}`);
    } catch (e) {
      showToast('error', userMessage(e));
    } finally {
      this.pending = null;
    }
  }

  private async change(user: AdminUser, kind: 'role' | 'root' | 'active', enabled: boolean, role = ''): Promise<void> {
    if (this.pending) return;
    this.pending = user.id;
    try {
      const right = kind === 'root' ? 'Root' : role === 'admin' ? 'Administrator' : roleLabel(role);
      // Switching an account off asks for a reason: it is what the person sees.
      let reason: string | undefined;
      if (kind === 'active' && !enabled) {
        const inheritor = this.data.roots.find((r) => r.id === this.transferTo)?.display_name ?? 'the selected Root';
        const answer = await confirmWithText({
          title: `Deactivate ${user.display_name}?`,
          message: `This account will lose access at once. Saved charts, dashboards and folders will move to ${inheritor}, and roles and group memberships are removed.`,
          field: {
            label: 'Reason (shown to them when they open the app)',
            placeholder: 'For example: Left the company. Contact IT to restore access.',
            maxLength: 500,
          },
          confirmLabel: 'Deactivate',
          destructive: true,
        });
        if (answer === null) return;
        reason = answer;
      } else if ((kind !== 'role' || role === 'admin' || (!enabled && this.stillInherited(user, role).length)) && !(await confirmAction({
        title: kind === 'active' ? `${enabled ? 'Reactivate' : 'Deactivate'} ${user.display_name}?` : `${enabled ? 'Grant' : 'Remove'} ${right} rights?`,
        message: kind === 'active'
          ? enabled ? 'This account can use the app again. It comes back with no roles: choose an Explorer role, and add it back to its groups.'
            : `This account will lose access. Saved charts, dashboards and folders will move to ${this.data.roots.find((r) => r.id === this.transferTo)?.display_name ?? 'the selected Root'}.`
          : `${user.display_name} will ${enabled ? 'receive' : 'lose their directly assigned'} ${right}${kind === 'root' || role === 'admin' ? ' rights' : ''}.${kind === 'root' ? ' Root can appoint administrators and other Root accounts.' : !enabled && this.stillInherited(user, role).length ? ` They still inherit it: ${this.stillInherited(user, role).join(', ')}.` : ' Inherited roles remain in effect.'}`,
        confirmLabel: kind === 'active' ? enabled ? 'Reactivate' : 'Deactivate' : enabled ? `Grant ${right}` : `Remove ${right}`,
        destructive: !enabled,
      }))) return;
      const updated = kind === 'role' ? await setUserRole(user.id, role, enabled)
        : kind === 'root' ? await setUserRoot(user.id, enabled)
          : await setUserActive(user.id, enabled, enabled ? undefined : this.transferTo, reason);
      await this.absorbUser(updated);
      showToast('success', `${user.display_name}'s access updated`);
      if (canDo('users:read')) await this.load();
    } catch (e) {
      showToast('error', userMessage(e));
    } finally {
      this.pending = null;
    }
  }

  private renderAccessTable(user: AdminUser): TemplateResult {
    if (!user.is_active) return html`<p class="admin-note">Account inactive; access is blocked.</p>`;
    if (this.accessError) return html`<p class="admin-note" role="alert">Effective access could not be read: ${this.accessError}</p>`;
    const access = this.access?.user_id === user.id ? this.access : null;
    if (!access) return html`<p class="admin-note" role="status">Working out effective access…</p>`;
    const groups = [...new Set(access.capabilities.map((c) => c.group))];
    return html`<table class="people-table access-table">
      <thead><tr><th scope="col">Capability</th><th scope="col">Status</th><th scope="col">Why</th></tr></thead>
      ${groups.map((group) => html`<tbody>
        <tr class="access-group"><th scope="rowgroup" colspan="3">${group}</th></tr>
        ${access.capabilities.filter((c) => c.group === group).map((c) => html`<tr class=${c.allowed ? 'is-allowed' : 'is-blocked'}>
          <td>${c.label}${c.note ? html`<span class="people-muted">${c.note}</span>` : nothing}</td>
          <td><span class="access-state">${c.allowed ? 'Allowed' : 'Blocked'}</span></td>
          <td>${c.reason}${!c.allowed && c.sources.length ? html`<span class="people-muted">Role still held: ${c.sources.join(', ')}</span>` : nothing}</td>
        </tr>`)}
      </tbody>`)}
    </table>`;
  }

  private renderDetails(user: AdminUser): TemplateResult {
    const writable = canDo('users:write') && (isRootUser.value || !user.is_root);
    const busy = !!this.pending || this.loading;
    const otherRoots = this.data.roots.filter((r) => r.id !== user.id);
    const bases = rolesOfKind('base');
    const modules = rolesOfKind('module');
    const current = baseRole(user);
    const inherited = inheritedRoles(user);
    const sharing = user.bindings.filter((b) => !(b.scope === 'global' && APP_KINDS.has(b.role_kind)));
    return html`<div class="people-detail">
      <div class="people-facts">
        <span>First seen <strong>${when(user.first_seen_at)}</strong></span>
        <span>Last activity <strong>${when(user.last_seen_at)}</strong></span>
        <span>Username <strong>${user.username ?? 'Not recorded'}</strong></span>
        <span>Last app version <strong>${user.last_app_version ?? 'Not recorded'}</strong></span>
        ${user.is_service ? html`<span>Service account</span>` : nothing}
        ${user.deactivated_at ? html`<span>Deactivated <strong>${when(user.deactivated_at)}</strong></span>` : nothing}
        ${!user.is_active && user.deactivation_reason ? html`<span>Reason <strong>${user.deactivation_reason}</strong></span>` : nothing}
      </div>
      <div class="people-access">
        <div class="people-controls">
          <h2>Explorer role</h2>
          <select class="share-role people-base" aria-label=${`Explorer role for ${user.display_name}`}
            .value=${current ?? ''} ?disabled=${busy || !user.is_active || !writable}
            @change=${(e: Event) => { const select = e.target as HTMLSelectElement; const value = select.value || null;
              select.value = current ?? ''; void this.changeBase(user, value); }}>
            ${bases.map((r) => html`<option value=${r.name} ?selected=${r.name === current}>${r.display_name}</option>`)}
            <option value="" ?selected=${!current}>No Explorer role</option>
          </select>
          ${user.is_active && !current && !inherited.some((b) => b.role_kind === 'base')
            ? html`<p class="admin-note">No Explorer role: this account cannot save or share anything. Features still decides what it can open.</p>` : nothing}

          <h2>Optional roles</h2>
          ${modules.map((r) => html`<label class="people-toggle">
            <span>${r.display_name}</span>
            <input class="admin-switch" type="checkbox" role="switch"
              aria-label=${`${r.display_name} for ${user.display_name}`} .checked=${directRole(user, r.name)}
              title=${r.description ?? ''}
              ?disabled=${busy || !user.is_active || !writable}
              @change=${(e: Event) => { const input = e.target as HTMLInputElement; const enabled = input.checked;
                input.checked = directRole(user, r.name); void this.change(user, 'role', enabled, r.name); }} />
          </label>`)}
          <label class="people-toggle"><span>Administrator <span class="admin-kind is-root">root only</span></span>
            <input class="admin-switch" type="checkbox" role="switch" aria-label=${`Administrator for ${user.display_name}`}
              .checked=${directRole(user, 'admin')}
              ?disabled=${busy || !user.is_active || !writable || !isRootUser.value}
              @change=${(e: Event) => { const input = e.target as HTMLInputElement; const enabled = input.checked;
                input.checked = directRole(user, 'admin'); void this.change(user, 'role', enabled, 'admin'); }} />
          </label>
          <label class="people-toggle"><span>Root <span class="admin-kind is-root">root only</span></span>
            <input class="admin-switch" type="checkbox" role="switch" aria-label=${`Root rights for ${user.display_name}`} .checked=${user.is_root}
              title=${user.is_root && otherRoots.length === 0 ? 'Appoint another Root before removing the last Root.' : 'Root can grant and remove Administrator and Root rights.'}
              ?disabled=${busy || !user.is_active || !isRootUser.value || (user.is_root && otherRoots.length === 0)}
              @change=${(e: Event) => { const input = e.target as HTMLInputElement; const enabled = input.checked;
                input.checked = user.is_root; void this.change(user, 'root', enabled); }} />
          </label>

          ${inherited.length ? html`<h2>Inherited from groups</h2>
            <div class="ff-chips access-chips">${inherited.map((b) => html`<button class="ff-chip is-group access-chip"
              title=${`Comes from ${b.group_name ?? 'a group'}. Remove it there, or take ${user.display_name} out of the group.`}
              @click=${() => b.group_id && openAdminGroup(b.group_id)}>
              ${b.role_name}<span class="people-muted">${b.group_name ?? 'group'}</span></button>`)}</div>` : nothing}

          <p class="admin-note">Direct roles add to what groups give. Choosing Viewer does not take away a Member role a group grants. Only Root grants Administrator and Root.</p>
          ${isRootUser.value ? html`<div class="people-account-control">
            ${user.is_active ? html`<label>Transfer saved work to
              <select class="share-role" aria-label="Root receiving saved work" .value=${this.transferTo} ?disabled=${busy || user.id === myUserId.value}
                @change=${(e: Event) => { this.transferTo = (e.target as HTMLSelectElement).value; }}>
                ${otherRoots.map((r) => html`<option value=${r.id}>${r.display_name}</option>`)}
              </select></label>` : nothing}
            <label class="people-toggle"><span>Account active</span>
              <input class="admin-switch" type="checkbox" role="switch" aria-label=${`Account active for ${user.display_name}`}
                .checked=${user.is_active}
                title=${user.id === myUserId.value ? 'You cannot deactivate your own account.' : user.is_active ? 'Switch off to deactivate; you will be asked for a reason.' : 'Switch on to reactivate.'}
                ?disabled=${busy || (user.is_active && (!this.transferTo || user.id === myUserId.value))}
                @change=${(e: Event) => { const input = e.target as HTMLInputElement; const enabled = input.checked;
                  input.checked = user.is_active; void this.change(user, 'active', enabled); }} />
            </label>
          </div>` : nothing}
        </div>
        <div class="people-sources">
          <h2>Effective access</h2>
          ${this.renderAccessTable(user)}
          <details class="access-technical">
            <summary>Technical details</summary>
            <h2>Shared with them</h2>
            ${sharing.length ? html`<ul class="people-binding-list">${sharing.map((b) => html`<li>
              <strong>${b.role_name}</strong> · ${b.source === 'user' ? 'Assigned to user' : b.source === 'everyone' ? 'Everyone' : `Group: ${b.group_name ?? 'Unknown group'}`}
              · ${b.scope === 'global' ? 'Whole app' : `${b.scope}: ${b.scope_name ?? 'Unavailable object'}`}
              <span class="people-muted">Granted ${when(b.granted_at)}${b.granted_by_name ? ` by ${b.granted_by_name}` : ''}${b.expires_at ? html` · Expires ${when(b.expires_at)}` : nothing}</span>
            </li>`)}</ul>` : html`<p class="admin-note">Nothing shared with them directly or through a group.</p>`}
            <h2>Role permissions</h2>
            <div class="admin-perm-list">${user.permissions.length ? user.permissions.map((p) => html`<code class="admin-perm">${p}</code>`)
              : html`<p class="admin-note">${user.is_active ? 'No stored permissions.' : 'Account inactive; access is blocked.'}</p>`}</div>
            ${this.access?.user_id === user.id ? html`<h2>After Features</h2>
              <div class="admin-perm-list">${this.access.effective_permissions.map((p) => html`<code class="admin-perm">${p}</code>`)}</div>` : nothing}
            ${user.entra_roles.length ? html`<p class="admin-note">Directory roles: ${user.entra_roles.join(', ')}. App access comes from the roles above.</p>` : nothing}
          </details>
        </div>
      </div>
    </div>`;
  }

  override render(): TemplateResult {
    if (!catalogAvailabilityKnown.value) return html`<div class="catalog-empty" role="status">Loading users…</div>`;
    if (!catalogAvailable.value) return html`<div class="catalog-empty"><p class="catalog-empty-title">Users need the metadata database</p><p>The database is not configured or could not be reached.</p></div>`;
    if (!canDo('users:read')) return html`<div class="catalog-empty"><p class="catalog-empty-title">You do not have access to the user directory</p><p>Ask Root for user administration access.</p></div>`;
    return html`<section class="admin-section people" aria-busy=${this.loading || !!this.pending}>
      <div class="activity-filters">
        <input class="catalog-search" type="search" aria-label="Search users" placeholder="Search name, email or username…"
          maxlength="100" .value=${this.search} ?disabled=${!!this.pending} @input=${(e: Event) => this.searchChanged((e.target as HTMLInputElement).value)} />
        <select class="share-role" aria-label="Account status" .value=${this.status} ?disabled=${!!this.pending}
          @change=${(e: Event) => { this.status = (e.target as HTMLSelectElement).value; this.page = 0; void this.load(); }}>
          <option value="">All accounts</option><option value="active">Active</option><option value="inactive">Inactive</option>
        </select>
        <button class="catalog-btn" ?disabled=${this.loading || !!this.pending} @click=${() => void this.load()}>Refresh</button>
      </div>
      <p class="admin-note people-count" role="status">${this.data.total} ${this.data.total === 1 ? 'user' : 'users'}${this.loading ? ' · Loading…' : ''}${this.pending ? ' · Updating access…' : ''}</p>
      ${this.error ? html`<div class="catalog-empty" role="alert"><p class="catalog-empty-title">Users could not be loaded</p><p>${this.error}</p></div>`
        : this.data.users.length ? html`<div class="people-table-wrap"><table class="people-table">
          <thead><tr><th scope="col">User</th><th scope="col">Explorer role</th><th scope="col">Optional roles</th><th scope="col">Groups</th><th scope="col">Last login</th><th scope="col">Account</th></tr></thead>
          <tbody>${repeat(this.data.users, (u) => u.id, (user) => html`
            <tr class=${this.openId === user.id ? 'is-open' : ''}>
              <td><button class="people-name" aria-expanded=${this.openId === user.id} aria-controls=${`user-detail-${user.id}`}
                ?disabled=${!!this.pending} @click=${() => this.toggleDetails(user)}>${user.display_name}${user.id === myUserId.value ? html`<span class="admin-kind">you</span>` : nothing}</button><span class="people-muted">${user.email ?? user.username ?? 'No email recorded'}</span></td>
              <td>${user.is_root ? html`<span class="admin-role-chip is-root">Root</span>` : standing(user) === 'Administrator' ? html`<span class="admin-role-chip is-admin">Administrator</span>` : standing(user)}</td>
              <td>${optionalRoles(user).join(', ') || html`<span class="people-muted">None</span>`}</td>
              <td>${user.groups.map((g) => g.is_leader ? `${g.name} (leader)` : g.name).join(', ') || html`<span class="people-muted">None</span>`}</td>
              <td>${when(user.last_login_at, true)}</td>
              <td><span class="admin-kind">${user.is_active ? 'Active' : 'Inactive'}</span><button class="catalog-btn" aria-label=${`Details for ${user.display_name}`} ?disabled=${!!this.pending} @click=${() => this.toggleDetails(user)}>${this.openId === user.id ? 'Close' : 'Details'}</button></td>
            </tr>
            ${this.openId === user.id ? html`<tr id=${`user-detail-${user.id}`}><td colspan="6">${this.renderDetails(user)}</td></tr>` : nothing}
          `)}</tbody></table></div>`
          : html`<div class="catalog-empty"><p class="catalog-empty-title">${this.loading ? 'Loading users…' : this.search || this.status ? 'No matching users' : 'No users have signed in yet'}</p>${!this.loading && (this.search || this.status) ? html`<p>Clear the search or change the account filter.</p>` : nothing}</div>`}
      <p class="admin-note people-help">Last login shows how long ago the latest recorded app session started; hover for the exact UTC timestamp. Dates use your browser's time zone. New people start as ${roleLabel(roleCatalog.value?.default_role)} - see Roles.</p>
      ${this.data.total > PAGE_SIZE || this.page > 0 ? html`<div class="activity-pager">
        <button class="catalog-btn" ?disabled=${this.page === 0 || this.loading || !!this.pending} @click=${() => { this.page--; void this.load(); }}>Previous</button>
        <span>Page ${this.page + 1} of ${Math.max(1, Math.ceil(this.data.total / PAGE_SIZE))}</span>
        <button class="catalog-btn" ?disabled=${(this.page + 1) * PAGE_SIZE >= this.data.total || this.loading || !!this.pending} @click=${() => { this.page++; void this.load(); }}>Next</button>
      </div>` : nothing}
    </section>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'people-panel': PeoplePanel; }
}
